import React, {
  createRef,
  memo,
  ReactFragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CircleArrowLeft } from '../../assets/icons/CircleArrowLeft';
import { CircleArrowRight } from '../../assets/icons/CircleArrowRight';
import { useMergedClassNames } from '../../utils/HTMLutils';
import { scrollSmooth } from '../../utils/ScrollUtils';
import IconButton from '../iconButton/IconButton';
import './ScrollArrowOverflow.scss';
const rootClassName = 'scroll-arrow-overflow';

export interface ScrollArrowOverflowProps {
  children?: ReactFragment;
  arrows?: 'bottom' | 'left' | 'right';
  defaultOpened?: boolean;
}

function ScrollArrowOverflow(props: ScrollArrowOverflowProps) {
  const ref = createRef<HTMLDivElement>();
  const atualRef = useRef(0);

  const [end, setEnd] = useState(true);
  const atEnd = useRef(end);
  atEnd.current = end;

  const [start, setStart] = useState(true);
  const atStart = useRef(start);
  atStart.current = start;

  const handleScroll = useCallback(
    (delta: number) => {
      if (!ref.current) return;
      const container = ref.current;
      const unitSize = container.scrollWidth / container.childElementCount;
      const stringPrecision = (container.scrollLeft / unitSize).toString();
      const precision = parseFloat(
        stringPrecision.substring(0, stringPrecision.indexOf('.') + 4)
      );
      atualRef.current = Math.ceil(precision);
      const newLeft = (atualRef.current + delta) * unitSize;
      scrollSmooth({
        element: container,
        top: 0,
        left: newLeft,
        duration: 300,
        behavior: 'smooth',
      });
    },
    [ref]
  );

  const stickToAtual = useCallback(() => {
    if (!ref.current) return;
    const container = ref.current;
    const unitSize = container.scrollWidth / container.childElementCount;
    atualRef.current = Math.ceil(container.scrollLeft / unitSize);
    const newLeft = atualRef.current * unitSize;
    container.scrollLeft = newLeft;
  }, [ref]);

  useEffect(() => {
    window.addEventListener('resize', stickToAtual, false);
    return () => {
      window.removeEventListener('resize', stickToAtual, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback((evt?: any) => {
    const { target } = evt as { target: HTMLElement };

    if (target.scrollLeft === 0) {
      if (!atStart.current) {
        setStart(true);
      }
    } else if (atStart.current) {
      setStart(false);
    }
    if (target.offsetWidth + target.scrollLeft + 0.6 >= target.scrollWidth) {
      if (!atEnd.current) {
        setEnd(true);
      }
    } else if (atEnd.current) {
      setEnd(false);
    }
  }, []);

  useEffect(() => {
    const element = ref.current;
    t({ target: element });
    element?.addEventListener('scroll', t, false);
    return () => {
      element?.removeEventListener('scroll', t, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={useMergedClassNames([rootClassName, props.arrows])}>
      <div className={`${rootClassName}-navigation`}>
        <IconButton
          disabled={start}
          icon={<CircleArrowLeft />}
          onClick={() => {
            handleScroll(-1);
          }}
        />
        <IconButton
          disabled={end}
          icon={<CircleArrowRight />}
          onClick={() => {
            handleScroll(+1);
          }}
        />
      </div>
      <div ref={ref} className={`${rootClassName}-overflow`}>
        {props.children}
      </div>
    </div>
  );
}

ScrollArrowOverflow.defaultProps = {
  arrows: 'left',
  defaultOpened: true,
} as Partial<ScrollArrowOverflowProps>;

export default memo(ScrollArrowOverflow);
